import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, CheckCircle2, ChevronRight, Download, ExternalLink,
  FileText, Loader2, Save, Sparkles, Trash2,
} from "lucide-react";
import {
  getMcaWizard, createMcaDraft, updateMcaDraft, listMcaDrafts, deleteMcaDraft,
  downloadFilledMcaForm, getFilledMcaJson,
} from "@/lib/api";

/**
 * MCA Form Filing Wizard — generic, schema-driven, currently demonstrating
 * ADT-1. Same component will power INC-22 / MGT-14 / DPT-3 / etc. once
 * those forms are added to the backend's FORM_BUILDERS registry.
 *
 * URL: /companies/:companyId/file/:formKey?fy=2024-25[&draft=<id>]
 *
 * Flow:
 *   1. Auto-fill all sections from company.directors / professionals / resolutions.
 *   2. User reviews each section, edits fields inline.
 *   3. Save Draft (resume later) OR Generate Filled PDF + V3 JSON.
 *   4. Click "Open in MCA Portal" → opens V3 in a new tab; user uploads
 *      the JSON / pasted into V3 + attaches DSC + pays + submits.
 */
export default function McaFormWizard() {
  const { companyId, formKey } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const fyParam = searchParams.get("fy") || "";
  const draftParam = searchParams.get("draft") || "";

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [wizard, setWizard] = useState(null);
  const [edits, setEdits] = useState({});      // keyed 'sectionIdx.fieldIdx'
  const [activeSection, setActiveSection] = useState(0);
  const [draftId, setDraftId] = useState(draftParam || null);
  const [draftList, setDraftList] = useState([]);
  const [jsonPreview, setJsonPreview] = useState(null);
  const [extensionPresent, setExtensionPresent] = useState(false);
  const [extensionStatus, setExtensionStatus] = useState("");
  const [showExtensionHelp, setShowExtensionHelp] = useState(false);

  // Detect if the ROC Vault auto-fill Chrome extension is installed. The
  // extension uses TWO signals:
  //   (a) A synchronous `window.__rocVaultExtension` marker set on injection.
  //   (b) Repeated postMessage broadcasts (initial + 300/1000/3000 ms pulse)
  //       to catch listeners that mount after the bridge script runs.
  // We check both so we win the race regardless of script-load ordering.
  useEffect(() => {
    // Synchronous check — wins instantly if bridge ran before React mount.
    if (typeof window !== "undefined" && window.__rocVaultExtension?.installed) {
      setExtensionPresent(true);
    }
    const onMsg = (e) => {
      if (e.source !== window) return;
      const m = e.data;
      if (m?.type === "ROC_VAULT_EXTENSION_PRESENT") {
        setExtensionPresent(true);
      } else if (m?.type === "ROC_VAULT_AUTOFILL_ACK") {
        setExtensionStatus(m.ok ? "ready" : "error");
      }
    };
    window.addEventListener("message", onMsg);
    // Also re-check on focus — handles "install extension, then return to tab".
    const onFocus = () => {
      if (window.__rocVaultExtension?.installed) setExtensionPresent(true);
    };
    window.addEventListener("focus", onFocus);
    return () => {
      window.removeEventListener("message", onMsg);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      setErrorMsg("");
      try {
        const params = {};
        if (fyParam) params.fy = fyParam;
        if (draftId) params.draft_id = draftId;
        const r = await getMcaWizard(companyId, formKey, params);
        setWizard(r.data);
        if (r.data.draft?.field_values) setEdits(r.data.draft.field_values);
      } catch (e) {
        // Surface the actual reason so users (or you, debugging in prod) can
        // tell "form not deployed yet" from "company not found" from "network".
        const reason =
          e?.response?.data?.detail ||
          e?.response?.statusText ||
          e?.message ||
          "Unknown error";
        const status = e?.response?.status;
        setErrorMsg(
          status === 404
            ? `Form "${formKey}" not supported on this server (404). If you just deployed, ensure both backend and frontend were redeployed together.`
            : status === 401
              ? "Session expired — please sign in again."
              : `Failed to load form: ${reason}`,
        );
        toast.error("Could not load the wizard payload");
      } finally { setLoading(false); }
    })();
  }, [companyId, formKey, fyParam, draftId]);

  useEffect(() => {
    (async () => {
      try {
        const r = await listMcaDrafts(companyId, formKey);
        setDraftList(r.data || []);
      } catch { /* swallow */ }
    })();
  }, [companyId, formKey, busy]);

  const sections = wizard?.payload?.sections || [];
  const totalFields = sections.reduce((n, s) => n + (s.fields?.length || 0), 0);
  const editedCount = Object.keys(edits).length;
  const completion = totalFields > 0 ? Math.round((editedCount / totalFields) * 100) : 0;

  const setFieldValue = (sIdx, fIdx, val) => {
    setEdits((prev) => ({ ...prev, [`${sIdx}.${fIdx}`]: val }));
  };

  const getDisplayValue = (sIdx, fIdx, fallback) => {
    const key = `${sIdx}.${fIdx}`;
    return key in edits ? edits[key] : fallback;
  };

  const handleSaveDraft = async () => {
    setBusy(true);
    try {
      if (draftId) {
        await updateMcaDraft(companyId, formKey, draftId, {
          field_values: edits,
        });
        toast.success("Draft updated");
      } else {
        const r = await createMcaDraft(companyId, formKey, {
          form_key: formKey,
          financial_year: wizard?.payload?.financial_year || fyParam,
          field_values: edits,
        });
        setDraftId(r.data.id);
        setSearchParams({ fy: r.data.financial_year, draft: r.data.id }, { replace: true });
        toast.success("Draft saved — resume anytime");
      }
    } catch {
      toast.error("Could not save draft");
    } finally { setBusy(false); }
  };

  const handleDeleteDraft = async (id) => {
    if (!confirm("Delete this draft? This cannot be undone.")) return;
    try {
      await deleteMcaDraft(companyId, formKey, id);
      if (id === draftId) {
        setDraftId(null);
        setEdits({});
        setSearchParams({}, { replace: true });
      }
      toast.success("Draft deleted");
    } catch { toast.error("Could not delete draft"); }
  };

  const handleGeneratePdf = async () => {
    setBusy(true);
    try {
      await downloadFilledMcaForm(companyId, formKey, {
        financial_year: wizard?.payload?.financial_year || fyParam,
        field_values: edits,
      });
      toast.success("Filled-form PDF downloaded");
    } catch { toast.error("Could not generate filled PDF"); }
    finally { setBusy(false); }
  };

  const handleGenerateJson = async () => {
    setBusy(true);
    try {
      const r = await getFilledMcaJson(companyId, formKey, {
        financial_year: wizard?.payload?.financial_year || fyParam,
        field_values: edits,
      });
      setJsonPreview(r.data);
    } catch { toast.error("Could not generate JSON payload"); }
    finally { setBusy(false); }
  };

  const handleCopyJson = async () => {
    if (!jsonPreview) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(jsonPreview, null, 2));
      toast.success("JSON copied to clipboard");
    } catch { toast.error("Clipboard write failed"); }
  };

  // Unified flow: push payload to extension (if installed) AND open MCA in
  // one click. Replaces the old two-step "Send to V3 Offline" → "Open in MCA"
  // dance that confused users. If the extension is missing, we fall back to
  // just opening MCA + nudging the user to install.
  const handleOpenMca = async () => {
    if (!wizard?.mca_portal_url) return;

    // Push data to extension first (if installed) — fire and forget; we don't
    // block on the response so the MCA tab opens even if the extension errors.
    if (extensionPresent) {
      try {
        setBusy(true);
        const r = await getFilledMcaJson(companyId, formKey, {
          financial_year: wizard?.payload?.financial_year || fyParam,
          field_values: edits,
        });
        const flatFields = {};
        for (const [, sect] of Object.entries(r.data.fields || {})) {
          for (const [label, value] of Object.entries(sect || {})) {
            flatFields[label] = value;
          }
        }
        window.postMessage(
          {
            type: "ROC_VAULT_AUTOFILL",
            payload: {
              form_key: formKey.toLowerCase(),
              company_id: companyId,
              company_name: wizard?.payload?.company?.name,
              fields: flatFields,
            },
          },
          window.location.origin,
        );
        toast.success(
          `${Object.keys(flatFields).length} fields sent to extension — opening MCA…`,
        );
      } catch {
        toast.warning("Could not push data to extension — opening MCA anyway");
      } finally {
        setBusy(false);
      }
      // Small delay so the bridge has time to forward the payload before
      // the MCA tab opens (chrome.runtime messaging is sync but the
      // window.postMessage hop adds ~20ms).
      setTimeout(() => window.open(wizard.mca_portal_url, "_blank", "noopener"), 250);
    } else {
      // Extension not installed — open MCA + show the install nudge.
      window.open(wizard.mca_portal_url, "_blank", "noopener");
      toast.message(
        "MCA opened — install the ROC Vault extension to auto-fill on the form page",
        { duration: 5000 },
      );
      setShowExtensionHelp(true);
    }
  };

  // Push the auto-fill payload to the Chrome extension's storage. Then
  // open the MCA V3 Offline tab for this specific form. The extension's
  // content script picks up the payload and shows a "Fill from ROC Vault"
  // button on the V3 Offline page.
  const handleSendToV3Offline = async () => {
    if (!extensionPresent) {
      // Open the install-help modal instead of just toasting an error.
      setShowExtensionHelp(true);
      return;
    }
    setBusy(true);
    try {
      // Build the flat field map the extension expects.
      const r = await getFilledMcaJson(companyId, formKey, {
        financial_year: wizard?.payload?.financial_year || fyParam,
        field_values: edits,
      });
      const flatFields = {};
      for (const [, sect] of Object.entries(r.data.fields || {})) {
        for (const [label, value] of Object.entries(sect || {})) {
          flatFields[label] = value;
        }
      }
      window.postMessage(
        {
          type: "ROC_VAULT_AUTOFILL",
          payload: {
            form_key: formKey.toLowerCase(),
            company_id: companyId,
            company_name: wizard?.payload?.company?.name,
            fields: flatFields,
          },
        },
        window.location.origin,
      );
      // V3 Offline deep-link for the specific form.
      const v3Url = `https://www.mca.gov.in/v3offline/ui/#/form/${formKey.toUpperCase()}?formaction=new`;
      setTimeout(() => {
        window.open(v3Url, "_blank", "noopener");
        toast.success(
          `Sent ${Object.keys(flatFields).length} fields to extension — V3 Offline tab opened`,
        );
      }, 400);
    } catch {
      toast.error("Could not send payload to extension");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-zinc-400">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading wizard…
      </div>
    );
  }

  if (!wizard) {
    return (
      <div className="max-w-2xl mx-auto p-8" data-testid="mca-wizard-error">
        <Link
          to={`/companies/${companyId}`}
          className="text-xs text-zinc-500 hover:text-[#002FA7] flex items-center gap-1 mb-4"
        >
          <ArrowLeft size={12} /> Back to company
        </Link>
        <div className="border border-red-200 bg-red-50/40 p-6">
          <h2 className="text-lg font-semibold text-red-700 mb-2">Could not load this form</h2>
          <p className="text-sm text-zinc-700 mb-4">{errorMsg || "Unknown error"}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-3 py-1.5 bg-red-600 text-white text-xs hover:bg-red-700"
            data-testid="wizard-retry"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-8" data-testid="mca-wizard">
      {/* Header */}
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <Link
            to={`/companies/${companyId}`}
            className="text-xs text-zinc-500 hover:text-[#002FA7] flex items-center gap-1"
          >
            <ArrowLeft size={12} /> Back to company
          </Link>
          <h1 className="font-heading text-3xl font-light tracking-tight mt-2">
            File {wizard.payload.form_label || formKey.toUpperCase()}
          </h1>
          <div className="text-xs text-zinc-500 mt-1">
            {wizard.payload.section} · Due {wizard.payload.due}
          </div>
          <div className="text-sm text-zinc-700 mt-2">
            {wizard.payload.company?.name} <span className="text-zinc-400">·</span>
            FY {wizard.payload.financial_year}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleSaveDraft}
            disabled={busy}
            className="px-3 py-2 border border-zinc-300 text-xs hover:border-[#002FA7] hover:text-[#002FA7] flex items-center gap-1 bg-white disabled:opacity-50"
            data-testid="wizard-save-draft"
          >
            <Save size={12} /> {draftId ? "Update Draft" : "Save Draft"}
          </button>
          <button
            onClick={handleGeneratePdf}
            disabled={busy}
            className="px-3 py-2 border border-zinc-300 text-xs hover:border-[#002FA7] hover:text-[#002FA7] flex items-center gap-1 bg-white disabled:opacity-50"
            data-testid="wizard-pdf"
          >
            <Download size={12} /> Filled PDF
          </button>
          <button
            onClick={handleGenerateJson}
            disabled={busy}
            className="px-3 py-2 border border-zinc-300 text-xs hover:border-[#002FA7] hover:text-[#002FA7] flex items-center gap-1 bg-white disabled:opacity-50"
            data-testid="wizard-json"
          >
            <FileText size={12} /> V3 JSON
          </button>
          <button
            onClick={handleSendToV3Offline}
            disabled={busy}
            className={`px-3 py-2 border text-xs flex items-center gap-1 disabled:opacity-50 ${
              extensionPresent
                ? "border-emerald-600 text-emerald-700 hover:bg-emerald-50 bg-white"
                : "border-zinc-300 text-zinc-500 bg-white hover:border-emerald-600 hover:text-emerald-700"
            }`}
            data-testid="wizard-send-extension"
            title={
              extensionPresent
                ? "Auto-fills MCA V3 Offline form via the ROC Vault Chrome extension"
                : "Install the ROC Vault Chrome extension to enable auto-fill"
            }
          >
            <Sparkles size={12} /> {extensionPresent ? "Send to V3 Offline" : "Install extension"}
          </button>
          <button
            onClick={handleOpenMca}
            disabled={busy}
            className="px-3 py-2 bg-[#002FA7] text-white text-xs hover:bg-[#003fd0] disabled:opacity-60 flex items-center gap-1"
            data-testid="wizard-open-mca"
            title={
              extensionPresent
                ? "Pushes auto-fill data to the ROC Vault Chrome extension and opens the MCA V3 portal in a new tab. On the MCA form page, click the floating 'Fill from ROC Vault' button to auto-populate."
                : "Opens the MCA V3 portal login. Install the ROC Vault Chrome extension to also auto-fill the form."
            }
          >
            <ExternalLink size={12} /> {extensionPresent ? "Auto-fill on MCA" : "Open in MCA"}
          </button>
          {wizard?.mca_portal_fallback_url && (
            <button
              onClick={() => window.open(wizard.mca_portal_fallback_url, "_blank", "noopener")}
              className="px-3 py-2 border border-zinc-300 text-zinc-600 text-xs hover:border-amber-500 hover:text-amber-700 flex items-center gap-1"
              data-testid="wizard-open-mca-fallback"
              title="V2 legacy portal — use this only if MCA V3 portal shows an 'internal application error'. The MCA V3 portal occasionally returns 5xx errors mid-day; your data in ROC Vault is unaffected."
            >
              <ExternalLink size={12} /> V2 fallback
            </button>
          )}
        </div>
      </div>

      {/* Progress */}
      <div className="border border-blue-200 bg-blue-50/40 px-4 py-3 mb-4 flex items-center gap-3">
        <Sparkles className="w-4 h-4 text-[#002FA7]" />
        <div className="flex-1 text-xs text-zinc-700">
          <strong className="text-[#002FA7]">{totalFields - editedCount}</strong> of {totalFields} fields auto-filled
          from ROC Vault data · You've reviewed/edited <strong>{editedCount}</strong>.
        </div>
        <div className="text-[10px] uppercase tracking-wider text-zinc-500">{completion}% reviewed</div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
        {/* Section nav */}
        <aside className="border border-zinc-200 bg-white">
          <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-zinc-500 border-b">
            Form Sections
          </div>
          {sections.map((s, i) => (
            <button
              key={i}
              onClick={() => setActiveSection(i)}
              className={`w-full text-left px-3 py-2 text-xs border-b last:border-b-0 flex items-center justify-between hover:bg-blue-50 ${
                activeSection === i ? "bg-blue-50 text-[#002FA7] font-medium" : ""
              }`}
              data-testid={`wizard-nav-${i}`}
            >
              <span>{s.title}</span>
              <ChevronRight size={12} />
            </button>
          ))}
          {draftList.length > 0 && (
            <div className="border-t border-zinc-200 p-3 bg-zinc-50/40">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Saved Drafts</div>
              <ul className="space-y-1">
                {draftList.map((d) => (
                  <li key={d.id} className="flex items-center justify-between text-[11px]">
                    <button
                      onClick={() => setSearchParams({ fy: d.financial_year, draft: d.id })}
                      className={`text-left flex-1 truncate ${d.id === draftId ? "text-[#002FA7] font-medium" : "text-zinc-700 hover:text-[#002FA7]"}`}
                    >
                      {d.financial_year} — {d.status}
                    </button>
                    <button
                      onClick={() => handleDeleteDraft(d.id)}
                      className="text-zinc-400 hover:text-red-600 p-0.5"
                    >
                      <Trash2 size={11} />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>

        {/* Section editor */}
        <main className="border border-zinc-200 bg-white p-6">
          {sections.map((s, sIdx) => (
            <div key={sIdx} className={sIdx === activeSection ? "block" : "hidden"}>
              <h2 className="text-lg font-semibold text-zinc-900 mb-1">{s.title}</h2>
              <p className="text-[11px] text-zinc-500 mb-5">
                Review each value. Edit anything that needs to change. Values pulled from
                Company Master / Resolutions / Registers are pre-filled.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4">
                {(s.fields || []).map((f, fIdx) => {
                  const v = getDisplayValue(sIdx, fIdx, f.value);
                  const userEdited = `${sIdx}.${fIdx}` in edits;
                  return (
                    <label key={fIdx} className="block">
                      <span className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1">
                        {f.label}
                        {userEdited && (
                          <CheckCircle2 size={10} className="text-emerald-600" />
                        )}
                      </span>
                      <input
                        value={v == null ? "" : String(v)}
                        onChange={(e) => setFieldValue(sIdx, fIdx, e.target.value)}
                        className="w-full border border-zinc-300 px-2.5 py-1.5 text-sm mt-0.5 focus:outline-none focus:ring-2 focus:ring-[#002FA7] bg-white"
                        data-testid={`wizard-field-${sIdx}-${fIdx}`}
                      />
                      {f.hint && (
                        <span className="text-[10px] text-zinc-500 italic block mt-0.5">
                          {f.hint}
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>

              <div className="mt-6 pt-4 border-t border-zinc-100 flex items-center justify-between">
                <button
                  onClick={() => setActiveSection(Math.max(0, activeSection - 1))}
                  disabled={activeSection === 0}
                  className="px-3 py-1.5 border border-zinc-300 text-xs hover:border-[#002FA7] flex items-center gap-1 disabled:opacity-30"
                >
                  <ArrowLeft size={12} /> Previous
                </button>
                <span className="text-[11px] text-zinc-400">
                  Section {sIdx + 1} of {sections.length}
                </span>
                {sIdx === sections.length - 1 ? (
                  <button
                    onClick={handleGeneratePdf}
                    disabled={busy}
                    className="px-4 py-1.5 bg-[#002FA7] text-white text-xs hover:bg-[#003fd0] flex items-center gap-1"
                    data-testid="wizard-finish"
                  >
                    Generate Filled PDF <ArrowRight size={12} />
                  </button>
                ) : (
                  <button
                    onClick={() => setActiveSection(activeSection + 1)}
                    className="px-3 py-1.5 border border-[#002FA7] text-[#002FA7] text-xs hover:bg-[#002FA7]/5 flex items-center gap-1"
                    data-testid={`wizard-next-${sIdx}`}
                  >
                    Next <ArrowRight size={12} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </main>
      </div>

      {/* JSON preview modal */}
      {jsonPreview && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-6" onClick={() => setJsonPreview(null)}>
          <div className="bg-white max-w-3xl w-full max-h-[80vh] overflow-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="wizard-json-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold">V3 Portal JSON Payload</h3>
              <button onClick={() => setJsonPreview(null)} className="text-zinc-400 hover:text-zinc-900">×</button>
            </div>
            <p className="text-[11px] text-zinc-500 mb-3">
              Copy this and paste into MCA V3's "Upload pre-filled JSON" box. (For forms that support pre-fill upload.)
            </p>
            <pre className="text-[11px] font-mono bg-zinc-50 p-3 border border-zinc-200 overflow-auto max-h-[50vh]">{JSON.stringify(jsonPreview, null, 2)}</pre>
            <div className="mt-3 flex justify-end gap-2">
              <button onClick={handleCopyJson} className="px-3 py-1.5 bg-[#002FA7] text-white text-xs">Copy JSON</button>
              <button onClick={() => setJsonPreview(null)} className="px-3 py-1.5 border border-zinc-300 text-xs">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Install-extension help modal */}
      {showExtensionHelp && (
        <div
          className="fixed inset-0 bg-black/50 z-40 flex items-center justify-center p-6"
          onClick={() => setShowExtensionHelp(false)}
        >
          <div
            className="bg-white max-w-2xl w-full max-h-[85vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="wizard-extension-help-modal"
          >
            <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-[#002FA7]">
                Install ROC Vault Auto-fill (Chrome Extension)
              </h3>
              <button onClick={() => setShowExtensionHelp(false)} className="text-zinc-400 hover:text-zinc-900 text-xl">×</button>
            </div>
            <div className="px-6 py-5 text-sm leading-relaxed text-zinc-800">
              <p className="mb-4">
                The auto-fill feature uses a small Chrome plug-in that reads ROC Vault data and
                fills the MCA V3 Offline form automatically. <strong>One-time setup, 5 minutes.</strong>
              </p>

              <ol className="space-y-4">
                <li className="flex gap-3">
                  <span className="w-7 h-7 bg-[#002FA7] text-white text-xs flex items-center justify-center rounded-full shrink-0 font-semibold">1</span>
                  <div className="flex-1">
                    <div className="font-medium">Download the plug-in</div>
                    <p className="text-xs text-zinc-600 mt-0.5 mb-2">
                      Saves a ZIP file (~10 KB) to your computer.
                    </p>
                    <a
                      href="/api/chrome-extension/download"
                      download
                      className="inline-flex items-center gap-2 px-4 py-2 bg-[#002FA7] text-white text-xs hover:bg-[#003fd0]"
                      data-testid="wizard-download-extension"
                    >
                      <Download size={14} /> Download Extension (ZIP)
                    </a>
                  </div>
                </li>

                <li className="flex gap-3">
                  <span className="w-7 h-7 bg-[#002FA7] text-white text-xs flex items-center justify-center rounded-full shrink-0 font-semibold">2</span>
                  <div className="flex-1">
                    <div className="font-medium">Unzip the file</div>
                    <p className="text-xs text-zinc-600 mt-0.5">
                      Right-click → Extract All (Windows) or double-click (Mac). You'll get a folder named <code className="px-1 py-0.5 bg-zinc-100 font-mono text-[11px]">roc-vault-autofill-extension</code>.
                    </p>
                  </div>
                </li>

                <li className="flex gap-3">
                  <span className="w-7 h-7 bg-[#002FA7] text-white text-xs flex items-center justify-center rounded-full shrink-0 font-semibold">3</span>
                  <div className="flex-1">
                    <div className="font-medium">Open Chrome Extensions page</div>
                    <p className="text-xs text-zinc-600 mt-0.5">
                      Paste this into your address bar:
                    </p>
                    <code className="block mt-1 px-3 py-1.5 bg-zinc-100 font-mono text-[11px] select-all">chrome://extensions</code>
                  </div>
                </li>

                <li className="flex gap-3">
                  <span className="w-7 h-7 bg-[#002FA7] text-white text-xs flex items-center justify-center rounded-full shrink-0 font-semibold">4</span>
                  <div className="flex-1">
                    <div className="font-medium">Enable "Developer mode"</div>
                    <p className="text-xs text-zinc-600 mt-0.5">
                      Toggle on the switch in the top-right corner of the Extensions page.
                    </p>
                  </div>
                </li>

                <li className="flex gap-3">
                  <span className="w-7 h-7 bg-[#002FA7] text-white text-xs flex items-center justify-center rounded-full shrink-0 font-semibold">5</span>
                  <div className="flex-1">
                    <div className="font-medium">Click "Load unpacked"</div>
                    <p className="text-xs text-zinc-600 mt-0.5">
                      A folder picker opens. Select the <strong>unzipped folder</strong> from step 2.
                      The extension icon appears in your Chrome toolbar.
                    </p>
                  </div>
                </li>

                <li className="flex gap-3">
                  <span className="w-7 h-7 bg-emerald-600 text-white text-xs flex items-center justify-center rounded-full shrink-0 font-semibold">✓</span>
                  <div className="flex-1">
                    <div className="font-medium text-emerald-700">Reload this page</div>
                    <p className="text-xs text-zinc-600 mt-0.5">
                      Press <kbd className="px-1.5 py-0.5 bg-zinc-100 font-mono text-[10px] border border-zinc-300">F5</kbd>.
                      The "Install extension" button will turn green and say <strong>"Send to V3 Offline"</strong>.
                    </p>
                  </div>
                </li>
              </ol>

              <div className="mt-5 border border-blue-200 bg-blue-50/40 p-3 text-xs text-zinc-700">
                <strong className="text-[#002FA7]">What happens after install:</strong> Click "Send to V3 Offline"
                → MCA V3 Offline opens in a new tab → wait 1-2 seconds → a blue <em>"Fill from ROC Vault"</em>
                button appears bottom-right of that tab → click it → all 28 fields fill automatically. You then
                review, attach DSC, pay, and submit on MCA.
              </div>
            </div>
            <div className="px-6 py-3 border-t border-zinc-200 bg-zinc-50 flex justify-end">
              <button
                onClick={() => setShowExtensionHelp(false)}
                className="px-4 py-2 bg-[#002FA7] text-white text-xs"
              >
                Got it — close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
